import { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import translations from 'src/internationalisation/translation-map.json';
import { simulationChartType } from 'src/enums/simulations';
import { IProjectsOverview } from 'src/common/interfaces/project';
import { IReportRef, ISummaryData } from 'src/common/interfaces/report';
import forecastLogo from 'src/assets/logo-forecast.png';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { Operation, Section } from 'src/common/interfaces/activity-model';
import { selectCostCodesIdToCodeMap } from 'src/store/entities/cost-codes/selectors';
import { addTimeAndCostVsDepth } from 'views/report/pages/add-time-and-cost-vs-depth.ts';
import { addFooterToPages } from 'views/report/utils/add-footer.ts';
import { addHeaderToPages } from 'views/report/utils/add-header.ts';
import { addCoverPage } from 'views/report/pages/add-cover-page.ts';
import { addCostItemAfeOverview } from 'views/report/pages/add-cost-item-afe-overview/add-cost-item-afe-overview.ts';
import { magentaPlumColor, magentaPlumShadowColor, oliveColor, oliveShadowColor } from 'views/report/colors.ts';
import { forecastLogoWidth } from 'views/report/utils/style-consts.ts';
import { selectPrimaryCurrency } from 'store/entities/company-settings/selectors';
import type { TRootState } from 'src/store/store-types';
import { ResultChartGrid } from './report-chart';
import { addTitlePage } from './utils/add-title-page';
import { addSummaryPage } from './pages/add-summary-page.ts';
import { addSectionWaterfallTimePage } from './pages/add-section-waterfall-time.ts';
import { addSectionWaterfallCostPage } from './pages/add-section-waterfall-cost.ts';
import { addCumulativeProbabilityTimePage } from './pages/add-cumulative-probibility-time.ts';
import { addCumulativeProbabilityCostPage } from './pages/add-cumulative-probibility-cost.ts';
import { addTimeVsDepthPage } from './pages/add-time-vs-depth.ts';
import { addCostVsDepthPage } from './pages/add-cost-vs-depth.ts';
import { addRisksPage } from './pages/add-risks.ts';
import { addCostItemOverviewPage } from './pages/add-cost-itew-overview.ts';
import { toast } from '@oliasoft-open-source/react-ui-library';

type TRiskOptions = {
  value: string;
  label: string;
  selected: boolean;
};
type TRiskHeaderOptions = {
  type: string;
  label: string;
  value: null;
  selected: boolean;
};
export type TRiskFilter = Array<TRiskOptions | TRiskHeaderOptions>;

const Report = ({
  reportRef,
  simulationsResult,
  projectOverview,
  summaryData,
  operations,
  sectionItems,
  costCodesIdToCodeMap,
  primaryCurrency,
}: {
  reportRef: { current: IReportRef };
  simulationsResult: any;
  projectOverview: IProjectsOverview;
  summaryData: ISummaryData;
  operations: Operation[];
  sectionItems: Section[];
  costCodesIdToCodeMap: Map<string | null, string>;
  primaryCurrency: string;
}) => {
  const { t } = useTranslation();

  const hiddenContainerRef = useRef(null);

  const timeVsDepthAfeChartRef = useRef(null);
  const costVsDepthAfeChartRef = useRef(null);

  const waterfallTimeChartRef = useRef(null);
  const waterfallCostChartRef = useRef(null);
  const cumulativeProbabilityTimeChartRef = useRef(null);
  const cumulativeProbabilityCostChartRef = useRef(null);
  const timeVsDepthChartRef = useRef(null);
  const costVsDepthChartRef = useRef(null);
  const risksChartRef = useRef(null);

  const riskOperationOptions = operations.map((operation) => ({
    value: operation.operationId,
    label: operation.name,
    selected: true,
  }));
  const riskSectionOptions = sectionItems.map((section) => ({
    value: section.projectSectionId,
    label: section.name,
    selected: true,
  }));
  const riskFilter: TRiskFilter = [
    {
      type: 'Heading',
      label: t(translations.operations),
      value: null,
      selected: false,
    },
    ...riskOperationOptions,
    {
      type: 'Divider',
      label: '',
      value: null,
      selected: false,
    },
    {
      type: 'Heading',
      label: t(translations.sections),
      value: null,
      selected: false,
    },
    ...riskSectionOptions,
  ];

  const generateAfeReport = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const forecastLogoProps = pdf.getImageProperties(forecastLogo);
    const forecastLogoHeight = (forecastLogoProps.height * forecastLogoWidth) / forecastLogoProps.width;
    const padding = 10;

    addCoverPage(pdf, projectOverview);

    if (!isEmpty(simulationsResult)) {
      await addTimeAndCostVsDepth({
        doc: pdf,
        padding,
        forecastLogoHeight,
        timeVsDepthChartRef: timeVsDepthAfeChartRef,
        costVsDepthChartRef: costVsDepthAfeChartRef,
        simulationsResult,
        projectOverview,
        operations,
        riskFilter,
      });

      addCostItemAfeOverview({
        doc: pdf,
        simulationsResult,
        costCodesIdToCodeMap,
      });

      addHeaderToPages(pdf, projectOverview);
      addFooterToPages(pdf, projectOverview);
    }

    pdf.save(`${t(translations.projects_afeReport)}_${projectOverview?.name}_${projectOverview?.wellbore?.name}.pdf`);
    toast({
      message: {
        type: 'Success',
        content: t(translations.projects_afeReportPdfMessage),
      },
    });
  };

  const generateReport = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const forecastLogoProps = pdf.getImageProperties(forecastLogo);
    const forecastLogoHeight = (forecastLogoProps.height * forecastLogoWidth) / forecastLogoProps.width;
    const padding = 10;

    addTitlePage(pdf, projectOverview);
    addSummaryPage(pdf, padding, forecastLogoHeight, summaryData, primaryCurrency);

    if (!isEmpty(simulationsResult)) {
      await addSectionWaterfallTimePage(pdf, padding, forecastLogoHeight, waterfallTimeChartRef, simulationsResult);
      await addSectionWaterfallCostPage(
        pdf,
        padding,
        forecastLogoHeight,
        waterfallCostChartRef,
        simulationsResult,
        primaryCurrency,
      );
      await addCumulativeProbabilityTimePage(
        pdf,
        padding,
        forecastLogoHeight,
        cumulativeProbabilityTimeChartRef,
        simulationsResult,
      );
      await addCumulativeProbabilityCostPage(
        pdf,
        padding,
        forecastLogoHeight,
        cumulativeProbabilityCostChartRef,
        simulationsResult,
        primaryCurrency,
      );
      await addTimeVsDepthPage(pdf, padding, forecastLogoHeight, timeVsDepthChartRef, simulationsResult, operations);
      await addCostVsDepthPage(
        pdf,
        padding,
        forecastLogoHeight,
        costVsDepthChartRef,
        simulationsResult,
        primaryCurrency,
        operations,
      );
      await addRisksPage(pdf, padding, forecastLogoHeight, risksChartRef, riskFilter, operations);
      await addCostItemOverviewPage(
        pdf,
        padding,
        forecastLogoHeight,
        simulationsResult,
        costCodesIdToCodeMap,
        primaryCurrency,
      );
    }

    addHeaderToPages(pdf, projectOverview);
    addFooterToPages(pdf);

    pdf.save(
      `${t(translations.projects_estimateSummary)}_${projectOverview?.name}_${projectOverview?.wellbore?.name}.pdf`,
    );
    toast({
      message: {
        type: 'Success',
        content: `${t(translations.projects_estimateSummaryPdfMessage)}`,
      },
    });
  };

  useImperativeHandle(reportRef, () => ({
    generateReport, // Expose the function
    generateAfeReport,
  }));

  return (
    <div>
      {/* Hidden container to render the component */}
      <div
        ref={hiddenContainerRef}
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          opacity: 0,
        }}
      >
        <div ref={timeVsDepthAfeChartRef} style={{ width: '470px', height: '470px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.TIME_DEPTH,
            }}
            selectedDataEntity={0}
            riskFilter={riskFilter}
            scatterChartOptions={{
              showPoints: false,
              backgroundColor: oliveShadowColor,
              borderColor: oliveColor,
            }}
            isReport
          />
        </div>
        <div ref={costVsDepthAfeChartRef} style={{ width: '470px', height: '470px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.TIME_DEPTH,
            }}
            selectedDataEntity={1}
            riskFilter={riskFilter}
            scatterChartOptions={{
              showPoints: false,
              backgroundColor: magentaPlumShadowColor,
              borderColor: magentaPlumColor,
            }}
            isReport
          />
        </div>

        <div ref={waterfallTimeChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.SECTION_WATERFALL,
            }}
            selectedDataEntity={0}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={waterfallCostChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.SECTION_WATERFALL,
            }}
            selectedDataEntity={1}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={cumulativeProbabilityTimeChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_cumulativeProbability),
              value: simulationChartType.CUMULATIVE_PROBABILITY,
            }}
            selectedDataEntity={0}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={cumulativeProbabilityCostChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.CUMULATIVE_PROBABILITY,
            }}
            selectedDataEntity={1}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={timeVsDepthChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.TIME_DEPTH,
            }}
            selectedDataEntity={0}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={costVsDepthChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.TIME_DEPTH,
            }}
            selectedDataEntity={1}
            riskFilter={riskFilter}
            isReport
          />
        </div>
        <div ref={risksChartRef} style={{ width: '800px' }}>
          <ResultChartGrid
            selectedItem={{
              label: t(translations.simulations_sectionWaterfall),
              value: simulationChartType.RISKS,
            }}
            selectedDataEntity={0}
            riskFilter={riskFilter}
            isReport
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const {
    activityModel: { operations, sectionItems },
  } = entities;
  const costCodesIdToCodeMap = selectCostCodesIdToCodeMap({
    entities,
  } as TRootState);
  return {
    operations,
    sectionItems,
    costCodesIdToCodeMap,
    primaryCurrency: selectPrimaryCurrency(entities),
  };
};

const Container = connect(mapStateToProps)(Report);

export { Container as Report };
